<script setup lang="ts">
const query = defineModel<string>('query');
const props = withDefaults(defineProps<{
    loading?: boolean;
    results?: boolean;
}>(), {
    loading: false,
    results: false
});
const emit = defineEmits<{
    (e: 'search', value: string): void;
    (e: 'clear'): void;
}>();

const { $t } = useLocalization();

const input = ref<any>(null);
const focused = ref(false);
const visible = computed(() => focused.value && query.value.length > 0);

onMounted(async () =>
{
    document.addEventListener('click', onClick);
    window.addEventListener('keydown', onKeyDown);
});

onBeforeUnmount(() =>
{
    document.removeEventListener('click', onClick);
    window.removeEventListener('keydown', onKeyDown);
});

const clear = (): void =>
{
    emit('clear');
};

const clearAll = (): void =>
{
    query.value = '';
    clear();
};

const open = (): void =>
{
    focused.value = true;
};

const close = (): void =>
{
    focused.value = false;
};

const onClick = (e: Event): void =>
{
    if (!e.composedPath().find(p => p.id === 'omnibox'))
    {
        close();
    }
};

const onKeyDown = (event: KeyboardEvent): void =>
{
    if (event.ctrlKey && event.key === 'q')
    {
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
        input.value?.focus();
    }
};

defineExpose({
    close
});
</script>

<template>
    <div id="omnibox" class="d-flex align-items-stretch flex-fill">
        <div class="wrap" :class="{ 'focused': focused }">
            <button type="button" class="navbar-toggler ms-auto" @click="open">
                <i class="far fa-fw fa-magnifying-glass" />
            </button>
            <div class="input">
                <input
                    ref="input"
                    v-model="query"
                    type="text"
                    class="form-control"
                    :placeholder="$t('[[[Wyszukaj (Ctrl+Q)]]]')"
                    @focus="open"
                >
                <i class="icon fa-solid fa-magnifying-glass" />
                <i v-if="(query.length > 0)" class="clear fa-solid fa-circle-xmark text-danger pointer" @click="clearAll" />
            </div>
            <div v-if="visible" class="popup">
                <div class="results shadow scroll bg-body p-2 pb-0 rounded-2">
                    <div v-if="!props.results" class="d-flex justify-content-center py-5 mb-2">
                        {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
                    </div>
                    <slot />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
#omnibox {
    height: var(--ideo-header-height);

    .wrap {
        width: 100%;
        margin: auto 8px;

        .navbar-toggler {
            display: none;
        }

        .input {
            position: relative;

            input {
                padding-left: 36px;
                padding: 8px 8px 8px 36px;
            }
            .icon {
                position: absolute;
                top: 12px;
                left: 12px;
                color: var(--bs-body-color);
            }
            .clear {
                position: absolute;
                top: 12px;
                right: 12px;
            }
        }

        .popup {
            position: relative;

            .results {
                position: absolute;
                min-height: 100px;
                top: 4px;
                left: 0;
                right: -100%;
                max-height: 90vh;
                min-width: 824px;
                max-width: 1024px;
                border: 1px solid var(--bs-border-color);
                z-index: var(--ideo-dropdown-zindex);
            }
        }
    }
}

.desktop {
    #omnibox {
        .wrap {
            width: 50%;
        }
    }
}

.mobile {
    #omnibox {
        .wrap {
            .navbar-toggler {
                display: block;
            }
            .input {
                display: none;
            }

            &.focused {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                margin: 0;
                z-index: var(--ideo-dropdown-zindex);
                height: var(--ideo-header-height);
                background-color: var(--bs-body-bg);

                .navbar-toggler {
                    display: none;
                }
                .input {
                    display: block;
                    margin: 12px 10px 0 10px;
                }
                .popup {
                    .results {
                        position: fixed;
                        top: var(--ideo-header-height);
                        left: 0;
                        right: 0;
                        bottom: 0;
                        min-width: auto;
                        max-width: auto;
                        max-height: none;
                    }
                }
            }
        }
    }
}
</style>
